import {Paths} from "../util/url.js";
import {store as defaultStore} from '../store.js';
import {restGet} from "../util/http.js";


/**
 * Uniwersalny provider do wczytywania danych z REST serwera do STORE
 */
export class GenericProvider {

    constructor(props = {}) {
        this.store = props.store || defaultStore;
        this.routeName = props.routeName;
        this.name = props.name || this.routeName;
        this.metaStoreName = 'providersMeatData';
        this.storeName = props.storeName || this.name;
        this.pk = props.pk || 'id';
        this.pathPk = this.pk;
        this.url = props.url;
        this.paths = Paths.factory(props.paths || POLKURIER.paths);

        if (!this.routeName && !this.url) {
            throw 'Parametr routeName lub url jest wymagany';
        }

        if (!this.name) {
            throw 'Parametr name jest wymagany';
        }

        if (props.params) {
            this.setParams(props.params);
        }
    }

    get fetchedAt() {
        return this.getState()?.fetchedAt || 0;
    }

    get updatedAt() {
        return this.getState()?.updatedAt || 0;
    }

    /**
     * Ustawia parametry w queryString.
     * @param params
     * @returns {GenericProvider}
     */
    setParams(params) {
        this.setState({params});
        return this;
    }

    /**
     * Ustawia parametry w queryString.
     * @returns {Object}
     */
    getParams() {
        return this.getState()?.params || {};
    }

    /**
     * @returns {Store}
     */
    getStore() {
        return this.store;
    }

    /**
     * Zwraca wszystkie obiekty z kolekcji.
     * Wczytuje z serwera jeśli nie wczytano wcześniej.
     * @return {Promise<Array>}
     */
    async all() {
        const state = this.getState();
        if (!state.isFetched || state.isRequested) {
            let currentRequest = state.currentRequest;
            if (!currentRequest) {
                currentRequest = this.fetchAll();
                this.setState({currentRequest});
            }
            const data = await currentRequest;
            this.setState({currentRequest: null});
            return data;
        }
        return this.store.get(this.storeName, []);
    }

    /**
     * Zwraca wszystkie obiekty z kolekcji.
     * Wczytuje z serwera jeśli nie wczytano wcześniej.
     * @return {Promise<Array>}
     */
    async load() {
        return this.all();
    }

    /**
     * Wczytuje dane z serwera.
     * @return {Promise<Array>}
     */
    async reload() {
        return this.fetchAll();
    }

    /**
     * Zwraca wszystkie elementy kolekcji
     * [!!] NIE wczytuje kolekcji z serwera - kolekcja MUSI być wcześniej wczytana poprzez metodę all() lub preload()
     * @return {Array}
     */
    getAll() {
        return this.store.get(this.storeName, []);
    }

    /**
     * Zwraca obiekt z kolekcji
     * @param query
     * @return {Promise<Object>}
     */
    async get(query) {
        const all = await this.all();
        return _.find(all, query);
    }

    /**
     * Zwraca obiekt z kolekcji
     * [!!] NIE wczytuje kolekcji z serwera - kolekcja MUSI być wcześniej wczytana poprzez metodę all() lub preload()
     *
     * @param query
     */
    find(query) {
        return _.find(this.getAll(), query);
    }

    /**
     * Zwraca obiekt z kolekcji po kluczu głównym
     * @param pkValue
     * @return {Promise<Object>}
     */
    async getByPk(pkValue) {
        return this.get({
            [this.pk]: pkValue
        });
    }

    /**
     * Zwraca obiekt z kolekcji po kluczu głównym
     * [!!] NIE wczytuje kolekcji z serwera - kolekcja MUSI być wcześniej wczytana poprzez metodę all() lub preload()
     *
     * @param pkValue
     */
    findByPk(pkValue) {
        return this.find({
            [this.pk]: pkValue
        });
    }

    /**
     * Zwraca url do pobrania danych
     * @param params
     * @return {string}
     */
    getUrl(params = {}) {
        if (this.url) {
            return this.url;
        }
        const defaultParams = this.getState().params || {};
        return this.paths.get(this.routeName, {...defaultParams, ...params});
    }

    /**
     * Wczytuje WSZYSTKIE obiekty z końcówki API
     * @return {Promise<[]>}
     */
    async fetchAll() {
        this.setState({
            isRequested: true,
            requestedAt: Date.now(),
        });

        let data = [];
        try {
            data = await this.fetchServerData();
            if (!Array.isArray(data)) {
                data = [data];
            }
            this.store.merge(this.storeName, this.prepareData(data), {
                sort: this.sortItemsBeforeAdd.bind(this),
            });
            this.setState({
                isRequested: false,
                isFetched: true,
                fetchedAt: Date.now(),
                updatedAt: Date.now(),
            });
        } catch (error) {
            this.setState({isRequested: false});
            throw error;
        }
        return data;
    }

    async fetchServerData() {
        return restGet(this.getUrl());
    }

    isFetchSingleRequested(pk) {
        return !!this.getFetchSingleRequest(pk)
    }

    getFetchSingleRequest(pk) {
        const fetchSingleRequest = this.getState().fetchSingleRequest || {};
        if (fetchSingleRequest[pk]) {
            return fetchSingleRequest[pk];
        }
    }

    setFetchSingleRequest(pk, request) {
        this.setState({
            fetchSingleRequest: {
                ...(this.getState().fetchSingleRequest || {}),
                [pk]: request,
            },
        });

        const fetchSingleRequest = this.getState().fetchSingleRequest || {};
        if (fetchSingleRequest[pk]) {
            return fetchSingleRequest[pk];
        }
    }

    /**
     * Wczytuje pojedynczy obiekt z końcówki API po kluczu głównym
     * @return {Promise<Object|null>}
     */
    async fetchSingle(pk, params = {}) {
        if (!pk) {
            return null;
        }

        let request = this.getFetchSingleRequest(pk);
        if (request) {
            return request;
        }

        try {
            request = restGet(this.getUrl({
                ...params,
                [this.pathPk]: pk,
            }));
            this.setFetchSingleRequest(pk, request);
            const model = await request;
            if (model) {
                this.add([model]);
            }
            return model;
        } catch (e) {
            return null;
        } finally {
            this.setFetchSingleRequest(pk, undefined);
        }
    }

    /**
     * Dodaje obiektu do kolekcji (np. po zapisie do bazy)
     * @param {Array} data
     */
    add(data) {
        if (!Array.isArray(data)) {
            data = [data];
        }
        this.store.merge(this.storeName, this.prepareData(data), {
            sort: this.sortItemsBeforeAdd.bind(this),
        });
        this.setState({updatedAt: Date.now()});
        return this;
    }

    sortItemsBeforeAdd(items) {
        return items;
    }

    /**
     * Dodaje element na początku kolekcji
     */
    prependElement(item) {
        this.store.merge(this.storeName, this.prepareData([item]), {prepend: true});
        this.setState({updatedAt: Date.now()});
        return this;
    }

    /**
     * usuwa obiekt do kolekcji (np. po usunięciu do bazy)
     * @param {Array} item
     */
    removeElement(item) {
        this.store.set(this.storeName, this.store.get(this.storeName, []).filter(m => m[this.pk] !== item[this.pk]));
        this.setState({updatedAt: Date.now()});
        return this;
    }

    /**
     * Wczytuje dane statycznie np. z TWIG
     * @param data
     */
    preload(data) {
        this.add(data);
        this.setState({
            isFetched: true,
            fetchedAt: Date.now(),
        });
    }

    /**
     * @param {array} data
     * @returns {array}
     */
    prepareData(data) {
        return data;
    }

    /**
     * Zwraca nazwę stora
     * @return {string}
     */
    getStoreName() {
        return this.storeName;
    }

    /**
     * Zwraca stan
     * @return {object}
     */
    getState() {
        return this.store.get(this.metaStoreName, {})[this.name] || {};
    }

    /**
     * Ustawia stan
     * @param props
     */
    setState(props) {
        this.store.merge(this.metaStoreName, {
            [this.name]: {
                ...this.getState(),
                ...props
            },
        });
    }

    /**
     * @returns {boolean}
     */
    isRequested() {
        return this.getState().isRequested === true;
    }

    /**
     * @returns {boolean}
     */
    isFetched() {
        return this.getState().isFetched === true;
    }

    /**
     * @param {Function} onChangeCallback
     * @param {Boolean} includeStateChanges
     * @returns {Object}
     */
    createStoreChangeEvents(onChangeCallback, includeStateChanges = true) {
        const events = {
            [`change:${this.getStoreName()}`]: onChangeCallback,
        };
        if (includeStateChanges === true) {
            events[`change:${this.metaStoreName}:${this.name}`] = onChangeCallback;
        }
        return events;
    }

}
